'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SliderConfig, TransformedNewsItem, Row } from '@/lib/types/pageBuilder';
import RowRenderer from './RowRenderer';
import type { IncludeSectionApiData } from '@/lib/pageUtils';
interface SliderWidgetProps {
  slider: SliderConfig;
  collectionsData: Map<string, TransformedNewsItem[]>;
  includeSectionApiData?: IncludeSectionApiData;
  background?: string;
  backgroundImage?: {
    url: string;
    enabled: boolean;
  };
}
export default function SliderWidget(t0) {
  const $ = _c(80);
  const {
    slider,
    collectionsData,
    includeSectionApiData,
    background,
    backgroundImage
  } = t0;
  const [currentIndex, setCurrentIndex] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [items, setItems] = useState(t1);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);
  const {
    autoPlay: t2,
    autoPlayInterval: t3,
    showDots: t4,
    showArrows: t5,
    slidesToShow: slidesToShowConfig,
    slidesToScroll: t6,
    loop: t7,
    data_binding,
    rows
  } = slider;
  const autoPlay = t2 === undefined ? false : t2;
  const autoPlayInterval = t3 === undefined ? 3000 : t3;
  const showDots = t4 === undefined ? true : t4;
  const showArrows = t5 === undefined ? true : t5;
  const slidesToScrollConfig = t6 === undefined ? 1 : t6;
  const loop = t7 === undefined ? true : t7;
  const slidesToScroll = showDots ? slidesToScrollConfig : 1;
  const t8 = slidesToShowConfig?.mobile ?? 1;
  const t9 = slidesToShowConfig?.tablet ?? 2;
  const t10 = slidesToShowConfig?.desktop ?? 4;
  let t11;
  if ($[1] !== t10 || $[2] !== t8 || $[3] !== t9) {
    t11 = {
      mobile: t8,
      tablet: t9,
      desktop: t10
    };
    $[1] = t10;
    $[2] = t8;
    $[3] = t9;
    $[4] = t11;
  } else {
    t11 = $[4];
  }
  const slidesToShow = t11;
  const [currentSlidesToShow, setCurrentSlidesToShow] = useState(slidesToShow.desktop);
  let t12;
  let t13;
  if ($[5] !== slidesToShow.desktop || $[6] !== slidesToShow.mobile || $[7] !== slidesToShow.tablet) {
    t12 = () => {
      const updateSlidesToShow = () => {
        if (window.innerWidth < 640) {
          setCurrentSlidesToShow(slidesToShow.mobile);
        } else {
          if (window.innerWidth < 1024) {
            setCurrentSlidesToShow(slidesToShow.tablet);
          } else {
            setCurrentSlidesToShow(slidesToShow.desktop);
          }
        }
      };
      updateSlidesToShow();
      window.addEventListener("resize", updateSlidesToShow);
      return () => window.removeEventListener("resize", updateSlidesToShow);
    };
    t13 = [slidesToShow.mobile, slidesToShow.tablet, slidesToShow.desktop];
    $[5] = slidesToShow.desktop;
    $[6] = slidesToShow.mobile;
    $[7] = slidesToShow.tablet;
    $[8] = t12;
    $[9] = t13;
  } else {
    t12 = $[8];
    t13 = $[9];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[10] !== collectionsData || $[11] !== data_binding) {
    t14 = () => {
      if (!data_binding?.api_endpoint) {
        setIsLoading(false);
        return;
      }
      let existingData = collectionsData.get(data_binding.api_endpoint);
      if (!existingData || existingData.length === 0) {
        for (const [key, value] of collectionsData.entries()) {
          if (key.includes(data_binding.api_endpoint) || data_binding.api_endpoint.includes(key)) {
            existingData = value;
            break;
          }
        }
      }
      if (existingData && existingData.length > 0) {
        const contentItems = existingData.filter(_temp);
        setItems(contentItems);
      }
      setIsLoading(false);
    };
    t15 = [data_binding, collectionsData];
    $[10] = collectionsData;
    $[11] = data_binding;
    $[12] = t14;
    $[13] = t15;
  } else {
    t14 = $[12];
    t15 = $[13];
  }
  useEffect(t14, t15);
  const maxIndex = loop ? items.length : Math.max(0, items.length - currentSlidesToShow);
  const totalDots = loop ? items.length : Math.max(1, items.length - currentSlidesToShow + 1);
  let t16;
  let t17;
  if ($[14] !== autoPlay || $[15] !== autoPlayInterval || $[16] !== isPaused || $[17] !== items.length || $[18] !== loop || $[19] !== maxIndex || $[20] !== slidesToScroll) {
    t16 = () => {
      if (!autoPlay || isPaused || items.length === 0) {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
          autoPlayRef.current = null;
        }
        return;
      }
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (loop) {
            return (prev + slidesToScroll) % items.length;
          }
          const next = prev + slidesToScroll;
          return next >= maxIndex ? 0 : next;
        });
      }, autoPlayInterval);
      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    };
    t17 = [autoPlay, autoPlayInterval, isPaused, items.length, maxIndex, loop, slidesToScroll];
    $[14] = autoPlay;
    $[15] = autoPlayInterval;
    $[16] = isPaused;
    $[17] = items.length;
    $[18] = loop;
    $[19] = maxIndex;
    $[20] = slidesToScroll;
    $[21] = t16;
    $[22] = t17;
  } else {
    t16 = $[21];
    t17 = $[22];
  }
  useEffect(t16, t17);
  let t18;
  if ($[23] !== items.length || $[24] !== loop || $[25] !== slidesToScroll) {
    t18 = () => {
      setCurrentIndex(prev_0 => {
        if (loop) {
          return (prev_0 - slidesToScroll + items.length) % items.length;
        }
        return Math.max(0, prev_0 - slidesToScroll);
      });
    };
    $[23] = items.length;
    $[24] = loop;
    $[25] = slidesToScroll;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const goToPrev = t18;
  let t19;
  if ($[27] !== items.length || $[28] !== loop || $[29] !== maxIndex || $[30] !== slidesToScroll) {
    t19 = () => {
      setCurrentIndex(prev_1 => {
        if (loop) {
          return (prev_1 + slidesToScroll) % items.length;
        }
        const next_0 = prev_1 + slidesToScroll;
        return next_0 >= maxIndex ? maxIndex : next_0;
      });
    };
    $[27] = items.length;
    $[28] = loop;
    $[29] = maxIndex;
    $[30] = slidesToScroll;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const goToNext = t19;
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = index => {
      setCurrentIndex(index);
    };
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const goToSlide = t20;
  let containerStyle;
  if ($[33] !== background || $[34] !== backgroundImage) {
    containerStyle = {};
    if (background) {
      containerStyle.backgroundColor = background;
    }
    if (backgroundImage?.enabled && backgroundImage?.url) {
      containerStyle.backgroundImage = `url(${backgroundImage.url})`;
      containerStyle.backgroundSize = "cover";
      containerStyle.backgroundPosition = "center";
      containerStyle.backgroundRepeat = "no-repeat";
    }
    $[33] = background;
    $[34] = backgroundImage;
    $[35] = containerStyle;
  } else {
    containerStyle = $[35];
  }
  if (isLoading) {
    let t21;
    if ($[36] !== currentSlidesToShow) {
      t21 = <div className="flex gap-4 animate-pulse">{Array.from({
          length: currentSlidesToShow
        }).map(_temp2)}</div>;
      $[36] = currentSlidesToShow;
      $[37] = t21;
    } else {
      t21 = $[37];
    }
    let t22;
    if ($[38] !== containerStyle || $[39] !== t21) {
      t22 = <div className="w-full p-4" style={containerStyle}>{t21}</div>;
      $[38] = containerStyle;
      $[39] = t21;
      $[40] = t22;
    } else {
      t22 = $[40];
    }
    return t22;
  }
  if (items.length === 0) {
    return null;
  }
  let t21;
  if ($[41] !== currentIndex || $[42] !== currentSlidesToShow || $[43] !== items || $[44] !== loop) {
    t21 = () => {
      const visibleItems = [];
      const count = loop ? currentSlidesToShow : Math.min(currentSlidesToShow, items.length - currentIndex);
      for (let i_0 = 0; i_0 < count; i_0++) {
        const index_0 = (currentIndex + i_0) % items.length;
        if (items[index_0]) {
          visibleItems.push(items[index_0]);
        }
      }
      return visibleItems;
    };
    $[41] = currentIndex;
    $[42] = currentSlidesToShow;
    $[43] = items;
    $[44] = loop;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const getVisibleItems = t21;
  let t22;
  if ($[46] !== currentIndex || $[47] !== data_binding || $[48] !== includeSectionApiData || $[49] !== rows) {
    t22 = (item_0, index_1) => {
      const itemCollectionsData = new Map();
      if (data_binding?.api_endpoint) {
        itemCollectionsData.set(data_binding.api_endpoint, [item_0]);
      }
      return <div key={`${item_0.id || index_1}-${currentIndex}`} className="flex-1 min-w-0">{rows.map(row => <RowRenderer key={row.id} row={row} collectionsData={itemCollectionsData} includeSectionApiData={includeSectionApiData} parentColumnData={[item_0]} dataStartIndex={0} />)}</div>;
    };
    $[46] = currentIndex;
    $[47] = data_binding;
    $[48] = includeSectionApiData;
    $[49] = rows;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const renderSlideItem = t22;
  let t23;
  let t24;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setIsPaused(true);
    t24 = () => setIsPaused(false);
    $[51] = t23;
    $[52] = t24;
  } else {
    t23 = $[51];
    t24 = $[52];
  }
  let t25;
  if ($[53] !== currentSlidesToShow || $[54] !== goToPrev || $[55] !== items.length || $[56] !== showArrows) {
    t25 = showArrows && items.length > currentSlidesToShow && <button onClick={goToPrev} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-colors" aria-label="Previous slide"><ChevronLeft className="w-5 h-5 text-gray-700" /></button>;
    $[53] = currentSlidesToShow;
    $[54] = goToPrev;
    $[55] = items.length;
    $[56] = showArrows;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== getVisibleItems || $[59] !== renderSlideItem) {
    t26 = <div ref={sliderRef} className="flex gap-4 transition-all duration-500 ease-in-out">{getVisibleItems().map((item_1, index_2) => renderSlideItem(item_1, index_2))}</div>;
    $[58] = getVisibleItems;
    $[59] = renderSlideItem;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== currentSlidesToShow || $[62] !== goToNext || $[63] !== items.length || $[64] !== showArrows) {
    t27 = showArrows && items.length > currentSlidesToShow && <button onClick={goToNext} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-colors" aria-label="Next slide"><ChevronRight className="w-5 h-5 text-gray-700" /></button>;
    $[61] = currentSlidesToShow;
    $[62] = goToNext;
    $[63] = items.length;
    $[64] = showArrows;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t25 || $[67] !== t26 || $[68] !== t27) {
    t28 = <div className="relative overflow-hidden px-4 py-4">{t25}{t26}{t27}</div>;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== currentIndex || $[71] !== currentSlidesToShow || $[72] !== items.length || $[73] !== showDots || $[74] !== totalDots) {
    t29 = showDots && items.length > currentSlidesToShow && <div className="flex justify-center gap-2 pb-4">{Array.from({
        length: Math.min(totalDots, 10)
      }).map((__0, index_3) => <button key={index_3} onClick={() => goToSlide(index_3)} className={`h-1 rounded-full transition-all duration-300 ${index_3 === currentIndex % totalDots ? "bg-blue-500 w-6" : "bg-gray-400 w-3 hover:bg-gray-300"}`} aria-label={`Go to slide ${index_3 + 1}`} />)}</div>;
    $[70] = currentIndex;
    $[71] = currentSlidesToShow;
    $[72] = items.length;
    $[73] = showDots;
    $[74] = totalDots;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== containerStyle || $[77] !== t28 || $[78] !== t29) {
    t30 = <div className="w-full relative" style={containerStyle} onMouseEnter={t23} onMouseLeave={t24}>{t28}{t29}</div>;
    $[76] = containerStyle;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  return t30;
}
function _temp2(_, i) {
  return <div key={i} className="flex-1 bg-gray-700/50 h-48 rounded-lg" />;
}
function _temp(item) {
  return !item._isCollectionMeta;
}